import { ComponentPropsWithRef, ReactNode } from 'react';
import styled, { createGlobalStyle, css } from 'styled-components';
import { CSS, PolymorphicComponentProps } from 'styled-components/dist/types';
import {
    colorVar,
    TextColors,
    Colors,
    fontSizeVar,
    FontSize,
    fontFamilyVar,
    radiusVar,
    Spacings,
    spacingVar,
    FontWeights,
    fontWeightVar,
} from './Global';

// #region Common
type CssSize = number | CSSUnitValue | undefined | `${number}%`;
type CssBoxAttr = `${'p' | 'm'}${'' | 'x' | 'y' | 't' | 'b' | 'l' | 'r'}`;
const convertCssSize = (value: CssSize | string, defaultValue: string = 'unset') =>
    typeof value === 'number' ? `${value}px` : value?.toString() || defaultValue;
const convertEdgeValues = (box: BoxModelEdges, prefix: 'p' | 'm') => {
    const edges = ['t', 'r', 'b', 'l'] as const;
    const orientations = ['y', 'x'] as const;
    const valueOrDefault = (edge: (typeof edges)[number], orientation: (typeof orientations)[number]) =>
        box[`${prefix}${edge}`] ?? box[`${prefix}${orientation}`] ?? box[prefix] ?? 0;
    const values = edges.flatMap((edge, i) => convertCssSize(valueOrDefault(edge, orientations[i % 2]), '0'));

    return values.join(' ');
};
type StyleOrEmpty = <TValue>(style: keyof CSS.PropertiesHyphen, value: TValue, varFn?: (value: Exclude<TValue, undefined>) => string) => string;
const styleOrEmpty: StyleOrEmpty = (style, value, varFn) => (!value ? '' : `${style}: ${varFn?.(value as Parameters<typeof varFn>[0]) ?? value};\n`);

type BoxModelEdges = {
    [key in CssBoxAttr]?: CssSize;
};
type BoxModelDims = {
    [key in `${'w' | 'h' | `${'min' | 'max'}${'H' | 'W'}`}`]?: CssSize;
};
type BoxModelProps = BoxModelEdges & BoxModelDims & { bg?: Colors; fg?: TextColors; noScroll?: boolean };
const boxEdges = (p: BoxModelEdges) => `
    padding: ${convertEdgeValues(p, 'p')};
    margin: ${convertEdgeValues(p, 'm')};
`;
const boxDims = (p: BoxModelDims) =>
    [
        [p.w, 'width'],
        [p.h, 'height'],
        [p.minW, 'min-width'],
        [p.minH, 'min-height'],
        [p.maxW, 'max-width'],
        [p.maxH, 'max-height'],
    ]
        .map(([value, name]) => styleOrEmpty(name as keyof CSS.PropertiesHyphen, value, convertCssSize))
        .join('\n');
const boxStyles = (p: BoxModelProps) => `
    ${boxEdges(p)}
    ${boxDims(p)}
    ${styleOrEmpty('background-color', p.bg, colorVar)}
    ${styleOrEmpty('color', p.fg, colorVar)}
    ${styleOrEmpty('overflow', p.noScroll ? 'hidden' : null)}
`;

type FontProps = { size?: FontSize; light?: true; color?: TextColors; mono?: boolean; italic?: boolean; weight?: FontWeights };
const fontStyles = (p: FontProps) => `
    ${styleOrEmpty('font-size', p.size, fontSizeVar)}
    ${styleOrEmpty('font-family', p.mono ? fontFamilyVar('mono') : null)}
    ${styleOrEmpty('color', p.light ? colorVar('text-dim') : p.color ? colorVar(p.color) : null)}
    ${styleOrEmpty('font-style', p.italic ? 'italic' : null)}
    ${styleOrEmpty('font-weight', p.weight, fontWeightVar)}
`;
// #endregion

// #region Layout
type FlexProps = { horz?: true; vert?: true; fill?: true; wrap?: boolean; gap?: Spacings };
const flexStyles = (p: FlexProps) => `
    ${styleOrEmpty('display', p.vert || p.horz ? 'flex' : '')}
    ${styleOrEmpty('flex-direction', 'horz' in p ? 'row' : 'vert' in p ? 'column' : '')};
    ${styleOrEmpty('flex', p.fill ? '1' : '0 0 content')};
    ${styleOrEmpty('flex-wrap', p.wrap ? 'wrap' : null)}
`;
type LayoutProps = FlexProps & BoxModelProps & { align?: CSSStyleDeclaration['alignItems']; justify?: 'start' | 'end' | 'center' | 'space-between' };
export const Layout = styled.div<LayoutProps>`
    ${flexStyles}
    ${(p) => styleOrEmpty('gap', p.gap, spacingVar)}
    align-items: ${(p) => p.align ?? 'stretch'};
    justify-content: ${(p) => p.justify || 'space-between'};
    height: ${(p) => ('vert' in p ? '100%' : 'auto')};
    min-height: 0;
    ${boxStyles}
`;

export const Sep = styled.div<{ thickness?: CssSize } & BoxModelProps & ({ vert: true } | { horz: true })>`
    border: none;
    border-color: #0000001f;
    border-style: solid;
    border-width: 0;
    flex: 0;
    align-self: stretch;
    border-left-width: ${(p) => ('vert' in p ? convertCssSize(p.thickness, '1px') : '0')};
    border-top-width: ${(p) => ('horz' in p ? convertCssSize(p.thickness, '1px') : '0')};
    ${boxStyles}
`;
// #endregion

// #region Overlays
export const DrawerGlobal = createGlobalStyle`
    html {
        overflow: hidden;
    }
`;

export const DrawerContainer = styled.div<{ visible?: boolean }>`
    position: fixed;
    top: 0;
    right: ${(p) => (p.visible ? '0' : '-100vw')};
    height: 100%;
    width: calc(100vw - 260px);
    transition: right 0.3s ease-in-out;
    background-color: #fff;
    box-shadow: -2px 0 5px rgba(0, 0, 0, 0.1);
    z-index: 1200; /* Above Redoc content, below popovers */
`;
export const DrawerOverlay = styled.div<{ visible?: boolean }>`
    position: fixed;
    top: 0;
    left: ${(p) => (p.visible ? '0vw' : '100vw')};
    height: 100vh;
    width: 100vw;
    opacity: ${(p) => (p.visible ? '1' : '0')};
    transition: opacity 0.3s ease-in-out;
    background-color: #0003;
`;
export const PopoverContent = styled.div`
    background: white;
    border: 1px solid #ccc;
    border-radius: 6px;
    box-shadow: 0 4px 12px #0001;
    padding: 12px;
`;

export const PopoverToolbar = styled(Layout).attrs({ horz: true })`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    padding: 8px 12px;
    margin: -12px;
    margin-top: 12px;
    gap: 6px;
    border-top: 1px solid #0002;
`;

// #endregion

// #region Text
export const H2 = styled.h2`
    font-weight: normal;
    font-size: 1.25rem;
    margin: 0;
    color: ${colorVar('text-primary')};
`;

export const Text = styled.span<BoxModelProps & FontProps & { block?: boolean; as?: string }>`
    ${boxStyles}
    ${fontStyles}
    ${(p) => styleOrEmpty('display', p.block ? 'block' : undefined)}
`;

export const Anchor = styled.a.attrs<BoxModelProps & FontProps & { block?: boolean }>({ role: 'link', tabIndex: 0 })`
    ${boxStyles}
    ${fontStyles}
    ${(p) => styleOrEmpty('display', p.block ? 'block' : 'inline')}
    color: ${colorVar('primary')};
    text-decoration: none;
    cursor: pointer;
    &:hover {
        text-decoration: underline;
    }
`;
//#endregion

// #region Forms
export function InputLbl({ children, text, as }: { children: ReactNode; text: ReactNode | string; as?: 'span' | 'label' | 'div' }) {
    return (
        <LblContainer as={as ?? 'label'}>
            {typeof text === 'string' ? <span className="--auto-lbl">{text}</span> : text}
            {children}
        </LblContainer>
    );
}
const LblContainer = styled.div<BoxModelProps & LayoutProps & { as?: string }>`
    ${boxStyles}
    ${(p) => flexStyles(p.vert || p.horz ? p : { vert: true, ...p })}
    gap: 4px;
    .--auto-lbl {
        font-size: 12px;
        color: #555;
    }
`;

export const Input = styled.input<BoxModelProps & FontProps>`
    outline: none;
    border-radius: ${radiusVar('md')};
    border: 1px solid #ccc;
    ${(p) => boxStyles({ p: 8, ...p })}
    ${(p) => fontStyles(p)}
    &:focus {
        border-color: ${colorVar('primary')};
    }
`;
export const TextArea = styled.textarea<BoxModelProps & FontProps>`
    outline: none;
    border-radius: ${radiusVar('md')};
    border: 1px solid #ccc;
    ${(p) => boxStyles({ p: 8, ...p })}
    ${(p) => fontStyles(p)}
    &:focus {
        border-color: ${colorVar('primary')};
    }
`;
export const Select = styled.select<BoxModelProps & FontProps>`
    appearance: none;
    outline: none;
    border-radius: ${radiusVar('md')};
    padding: 8px;
    border: 1px solid #ccc;
    ${(p) => boxStyles({ p: 8, ...p })}
    ${(p) => fontStyles(p)}    
    &:focus {
        border-color: ${colorVar('primary')};
    }
`;

export const CharButton = styled.button<{ sizePx?: number; fg?: TextColors; bg?: Colors }>`
    --size-factor: ${(p) => (p.sizePx ?? 24) / 24};
    font-size: calc(var(--icon-base-size) * var(--size-factor));
    border: none;
    line-height: calc(24px * var(--size-factor));
    min-height: calc(24px * var(--size-factor));
    min-width: calc(24px * var(--size-factor));
    max-height: calc(24px * var(--size-factor));
    max-width: calc(24px * var(--size-factor));
    text-align: center;
    vertical-align: middle;
    border-radius: calc(12px * var(--size-factor));
    transition: all 0.2s ease;
    ${(p) => styleOrEmpty('color', p.fg, colorVar)}
    cursor: pointer;
    background: ${(p) => (p.bg ? colorVar(p.bg) : 'transparent')};

    &:hover {
        box-shadow: 0 1px 3px #0002;
        background: #fff;
    }
`;
export const XButton = styled(CharButton)`
    &:before {
        content: '×';
    }
`;

export const Button = styled.button<{ type?: 'danger' | 'default' | 'primary' }>`
    cursor: pointer;
    padding: 10px 12px;
    border-radius: 6px;
    border: none;
    background-color: ${(p) =>
        colorVar(
            p.type === 'danger' ? 'danger' : p.type === 'primary' ? 'primary' : 'primary',
            (p.type ?? 'default') === 'default' ? 'bg' : undefined,
        )};
    color: ${(p) => colorVar(p.type === 'danger' ? 'white' : p.type === 'primary' ? 'white' : 'text-primary')};
    &:hover {
        filter: brightness(${(p) => ((p.type ?? 'default') === 'default' ? '1.06125' : '1.125')});
    }
`;

type ChipProps = {
    fill?: boolean;
    fg?: Colors;
    bg?: Colors;
    mono?: boolean;
};
const chipStyles = (p: ChipProps) => css`
    display: inline-flex;
    align-items: center;
    ${boxEdges({ p: 6, ...p })};
    border: 1px solid #0000;
    font-family: ${fontFamilyVar('mono')};
    border-radius: 3px;
    font-size: ${fontSizeVar('md')};
    ${styleOrEmpty('flex', p.fill)}
    ${styleOrEmpty('color', p.fg, colorVar)}
    ${styleOrEmpty('background-color', p.bg, colorVar)}
`;

export const ReadonlyChip = styled.span<ChipProps>`
    ${(p) => chipStyles({ bg: 'input-readonly', ...p })};
    color: #555;
`;

export const ChipButton = styled.button<ChipProps & BoxModelProps & { invalid?: boolean; active?: boolean }>`
    ${chipStyles}
    color: ${(p) => colorVar(p.invalid ? 'danger' : 'text-primary')};
    cursor: pointer;
    transition: all 0.2s ease;
    outline: none;
    background: #f8f8f8;
    --interacting-border: ${(p) => (p.invalid ? colorVar('danger-hover') : '#ccc')};
    --interacting-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    border-color: ${(p) => (p.active ? 'var(--interacting-border)' : '#ccc')};
    box-shadow: var(${(p) => (p.active ? '--interacting-shadow' : 'transparent')});

    &:hover {
        box-shadow: var(--interacting-shadow);
        border-color: var(--interacting-border);
    }

    &:focus {
        opacity: 0.9;
    }

    svg {
        height: 16px;
        width: 16px;
        margin-right: 4px;
    }
`;

export const Chip = styled(ChipButton)`
    padding: 0 8px;
    height: 24px;
    line-height: 24px;
    font-size: ${fontSizeVar('md')};
`;

export function ChipLbl({ children, text, ...props }: { children: ReactNode; text: string } & FlexProps) {
    return (
        <LblContainer {...props}>
            <ChipLblEl>{text}</ChipLblEl>
            {children}
        </LblContainer>
    );
}
const ChipLblEl = styled(Text)`
    font-size: ${fontSizeVar('xs')};
    color: ${(p) => colorVar(p.color ?? 'text-dim')};
`;
// #endregion
